import { FC, useMemo } from "react";

import { ArrowLeftIcon, ArrowRightIcon } from "../../../../assets/img/icons";
import Button from "../../../../components/Button";
import Calendar from "../../../../components/Calendar";
import IconButton from "../../../../components/IconButton";
import Text from "../../../../components/Text";
import Stat from "../../../../components/Text/Stat";
import {
    getFormattedDateInput,
    parseInputDateToLocal,
} from "../../../../utils/date";
import StatsDayCell from "./Cells/StatsDayCell";
import StatsOutsideCell from "./Cells/StatsOutsideCell";
import StatsValidatingCell from "./Cells/StatsValidatingCell";
import styles from "./styles.module.scss";

// Weekday custom pour ce calendrier uniquement : on affiche
// "Résultat hebdo" pour la colonne du samedi, on masque le dimanche
// et on garde les abréviations pour les autres jours.
const StatsWeekday = (props: any) => {
    const label = props.children as string;

    if (label === "Dim") {
        return <></>;
    }

    if (label === "Sam") {
        return (
            <th {...props}>
                <Text variant="body-bold">Résultat hebdo</Text>
            </th>
        );
    }

    return (
        <th {...props}>
            <Text variant="body-bold">{label}</Text>
        </th>
    );
};

export type DailyStats = {
    [key: string]: number | undefined;
};

type CalendarStatsProps = {
    month: Date;
    setMonth: (date: Date) => void;
    // Dates effectives déjà calculées au niveau de la page Stats
    startDate?: Date;
    endDate?: Date;
    dailyStats: DailyStats;
};

const CalendarStats: FC<CalendarStatsProps> = ({
    month,
    setMonth,
    startDate,
    endDate,
    dailyStats,
}) => {
    const getResultClass = (value: number | undefined) => {
        if (value === undefined) return styles.neutral;
        if (value > 0) return styles.positive;
        if (value < 0) return styles.negative;
        return styles.neutral;
    };

    const monthlyTotal = useMemo(() => {
        const year = month.getFullYear();
        const monthIndex = month.getMonth();
        let total = 0;

        Object.entries(dailyStats).forEach(([dateKey, value]) => {
            if (value === undefined) return;
            const date = parseInputDateToLocal(dateKey);
            if (
                !date ||
                date.getFullYear() !== year ||
                date.getMonth() !== monthIndex
            ) {
                return;
            }
            total += value;
        });

        return total;
    }, [dailyStats, month]);

    const header = (
        <div className={styles.calendarHeader}>
            <div className={styles.monthSelectorContainer}>
                <div className={styles.monthSelector}>
                    <IconButton
                        icon={ArrowLeftIcon}
                        size="sx"
                        onClick={() => {
                            const prev = new Date(month);
                            prev.setMonth(prev.getMonth() - 1);
                            setMonth(prev);
                        }}
                    />
                    <Text variant="body-bold" className={styles.currentMonth}>
                        {month.toLocaleString("default", {
                            month: "long",
                            year: "numeric",
                        })}
                    </Text>
                    <IconButton
                        icon={ArrowRightIcon}
                        size="sx"
                        onClick={() => {
                            const next = new Date(month);
                            next.setMonth(next.getMonth() + 1);
                            setMonth(next);
                        }}
                    />
                </div>
                <div className={styles.buttonsContainer}>
                    <Button
                        size="newSmall"
                        variant="secondary"
                        onClick={() => setMonth(new Date())}
                    >
                        Ce mois
                    </Button>
                </div>
            </div>
            <div className={styles.mensualStats}>
                <Text variant="body-bold">Résultat mensuel :</Text>
                <Stat type="currency" variant="body-bold" decimals={0}>
                    {monthlyTotal}
                </Stat>
            </div>
        </div>
    );

    const isDateInRange = (
        date: Date,
        startDate: Date,
        endDate: Date,
    ): boolean => {
        const startOfDay = (d: Date) =>
            new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        const dayTime = startOfDay(date);
        const rangeStart = startOfDay(startDate);
        const rangeEnd = startOfDay(endDate);
        return dayTime >= rangeStart && dayTime <= rangeEnd;
    };

    const getWeeklyTotal = (dateObj: Date): number | undefined => {
        const dayOfWeek = dateObj.getDay();
        const diff = dateObj.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        const monday = new Date(dateObj.setDate(diff));

        let total = 0;
        let hasData = false;

        for (let i = 0; i < 6; i++) {
            const currentDate = new Date(monday);
            currentDate.setDate(currentDate.getDate() + i);
            const dateKey = getFormattedDateInput(currentDate);
            const value = dailyStats[dateKey];
            if (value !== undefined) {
                total += value;
                hasData = true;
            }
        }

        return hasData ? total : undefined;
    };

    const shouldHideWeekRow = (weekDates: Date[]) => {
        if (!weekDates || weekDates.length === 0) return false;
        const hasInMonthVisibleDay = weekDates.some(
            (date) =>
                date.getMonth() === month.getMonth() && date.getDay() !== 0,
        );
        return !hasInMonthVisibleDay;
    };

    const isFutureDate = (date: Date) => {
        const today = new Date();
        const startOfToday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
        ).getTime();
        const dayTime = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
        ).getTime();
        return dayTime > startOfToday;
    };

    return (
        <Calendar
            className={styles.calendar}
            header={header}
            dayPickerProps={{
                month,
                showOutsideDays: true,
                components: {
                    Week: (weekProps) => {
                        const rawWeekDays: any[] =
                            weekProps?.week?.days ||
                            (weekProps as any)?.dates ||
                            (weekProps as any)?.days ||
                            [];
                        const weekDates: Date[] = rawWeekDays
                            .map((day) => day?.date ?? day)
                            .filter((day) => day instanceof Date);
                        const shouldHide = shouldHideWeekRow(weekDates);
                        const { children, ...rest } = weekProps as any;
                        return (
                            <tr
                                {...rest}
                                style={
                                    shouldHide
                                        ? { display: "none" }
                                        : rest.style
                                }
                            >
                                {children}
                            </tr>
                        );
                    },
                    Weekday: StatsWeekday,
                    Day: (props) => {
                        const dateObj = props.day.date;
                        const isOutside = props.className?.includes("rdp-outside");
                        const isSaturday = dateObj.getDay() === 6;
                        const isOutOfDateRange =
                            startDate && endDate
                                ? !isDateInRange(dateObj, startDate, endDate)
                                : false;

                        if (dateObj.getDay() === 0) {
                            return <div {...props} style={{ display: "none", padding: "6px" }} />;
                        }
                        if (isOutside) {
                            return (
                                <StatsOutsideCell
                                    {...props}
                                    className={styles.outsideCell}
                                />
                            );
                        }

                        if (isSaturday) {
                            // Calcul ISO week number
                            const target = new Date(dateObj);
                            target.setHours(0, 0, 0, 0);
                            target.setDate(target.getDate() + 4 - (target.getDay() || 7));
                            const yearStart = new Date(target.getFullYear(), 0, 1);
                            const weekNumber = Math.ceil(((target.getTime() - yearStart.getTime()) / 86400000 + yearStart.getDay() + 1) / 7);
                            // Afficher le total si le lundi de la semaine est passé ou aujourd’hui (pas le samedi)
                            const dayOfWeek = dateObj.getDay();
                            const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                            const mondayOfWeek = new Date(
                                dateObj.getFullYear(),
                                dateObj.getMonth(),
                                dateObj.getDate() - daysToMonday,
                            );
                            const isFuture = isFutureDate(mondayOfWeek);
                            const weeklyTotal = isFuture
                                ? undefined
                                : getWeeklyTotal(new Date(dateObj));
                            const valueClass = isFuture
                                ? styles.neutral
                                : getResultClass(weeklyTotal);
                            return (
                                <StatsValidatingCell
                                    weekNumber={weekNumber}
                                    weeklyTotal={weeklyTotal}
                                    className={valueClass}
                                />
                            );
                        }

                        const dateKey = getFormattedDateInput(dateObj);
                        const isFuture = isFutureDate(dateObj);
                        const value = isFuture ? undefined : dailyStats[dateKey];
                        const outOfRangeClass = isOutOfDateRange ? styles.outOfRangeCell : "";
                        const valueClass = isFuture
                            ? styles.neutral
                            : getResultClass(value);
                        return (
                            <StatsDayCell
                                {...props}
                                dayNumber={dateObj.getDate()}
                                value={value}
                                className={`${styles.cell} ${valueClass} ${outOfRangeClass}`}
                            />
                        );
                    },
                },
            }}
        />
    );
};

export default CalendarStats;
